import React, { useState, useEffect } from "react";
import useFetchObjects from "../../../api/useFetchObjects";
import { useAuth } from "../../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../../Components/ErrorPage";
import UserAdd from "./UserAdd";
import UserEdit from "./UserEdit";
import ChangePassword from "./ChangePassword";
import useDelete from "../../../api/useDelete";
import { useTranslation } from "react-i18next";

const Users = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["users", searchTerm, page, rowsPerPage],
    `admin/users/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("admin/users", token);

  useEffect(() => {
    refetch();
  }, [page, rowsPerPage, searchTerm, refetch]);

  const users = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : users.length;

  if (isError) {
    return <ErrorPage message="Failed to load users" />;
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("user.userList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("user.addUser")}
        </Button>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        Data loaded on {new Date().toLocaleString()}
      </Typography>
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("user.search")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 750 }} aria-label="user table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("user.id")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("user.username")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("user.email")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("user.firstName")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("user.lastName")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("user.phoneNumber")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("user.staff")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("user.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {["ID", "Username", "Email", "First Name", "Last Name", "Phone", "Staff", "Action"].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : users.map((user, index) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{user.username || "--"}</TableCell>
                    <TableCell>{user.email || "--"}</TableCell>
                    <TableCell>{user.first_name || "--"}</TableCell>
                    <TableCell>{user.last_name || "--"}</TableCell>
                    <TableCell>{user.phone_number || "--"}</TableCell>
                    <TableCell>{user.is_staff ? t("user.yes") : t("user.no")}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(user);
                          setOpenEdit(true);
                        }}
                      >
                        {t("user.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setChangePasswordData(user);
                          setOpenChangePassword(true);
                        }}
                      >
                        Change Password
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(user.id)}
                      >
                        {t("user.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center">
          <Typography>{t("user.rowsPerPage", "Rows Per Page")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
        />
      </Grid>
      {openAdd && (
        <UserAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}
      {openEdit && (
        <UserEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}
      {openChangePassword && (
        <ChangePassword
          open={openChangePassword}
          onClose={() => setOpenChangePassword(false)}
          userId={changePasswordData?.id}
          username={changePasswordData?.username}
          token={token}
        />
      )}
      <ConfirmDialog />
    </Box>
  );
};

export default Users;
